import { z } from 'zod';
import { UploadTarget } from './files.js';
import { PersonRef } from './people.js';
import { DesignScreen, DesignSetKind, DesignSetStatus, DevicePreset, Hotspot } from './showcase.js';
import { PrintSpec } from './showcase-print.js';
/**
 * Chapsi Showcase, phase C (docs/adr/0068): client guests, per-set roles, sharing, the public view-only link,
 * comments with replies, statuses and internal notes, attachments and view tracking.
 */
/** Invite and public link tokens: 22 random base62 characters (about 131 bits), stored only as a hash. */
export const SHOWCASE_TOKEN_LENGTH = 22;
export const ShowcaseToken = z.string().regex(/^[A-Za-z0-9]{22}$/, 'Unknown link');
/** What a client guest may do on a set. Team roles (Owner, Editor) are not stored. */
export const DesignGrantRole = z.enum(['view', 'comment', 'approve']);
export const DesignAccessRole = z.enum(['owner', 'editor', 'view', 'comment', 'approve']);
export const DESIGN_ROLE_LABELS = {
    view: 'View only',
    comment: 'Can comment',
    approve: 'Can approve',
    editor: 'Editor',
    owner: 'Owner',
};
export const DESIGN_ROLE_HELP = {
    view: 'Click through and view. No comments.',
    comment: 'View, pin comments and reply.',
    approve: 'Comment and sign off a version.',
    editor: 'Upload, link, reply, resolve, share.',
    owner: 'Everything, plus delete and billing.',
};
export const DesignVisibility = z.enum(['internal', 'shared']);
export const CommentStatus = z.enum(['open', 'in_progress', 'done', 'resolved']);
export const COMMENT_STATUS_LABELS = { open: 'Open', in_progress: 'In progress', done: 'Done', resolved: 'Resolved' };
/**
 * Who sets which status (the brief's table): anyone who can comment creates Open; the team sets In progress and Done;
 * the team or a client who can comment sets Resolved; a client may reopen their own comment.
 */
export function canSetCommentStatus(who, to) {
    if (who.team)
        return true;
    if (!who.canComment)
        return false;
    if (to === 'resolved')
        return true;
    if (to === 'open')
        return who.ownComment;
    return false;
}
export const MAX_COMMENT_LENGTH = 5000;
export const MAX_COMMENT_ATTACHMENTS = 5;
export const MAX_COMMENT_ATTACHMENT_MB = 10;
export const COMMENT_ATTACHMENT_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];
export const CommentAttachmentType = z.enum(COMMENT_ATTACHMENT_TYPES);
/** Seconds counted per view heartbeat at most (the page sends one every 15 seconds). */
export const MAX_VIEW_SECONDS = 60;
/**
 * Comment text as stored: control characters (other than new lines and tabs) removed, line endings normalised, and
 * trimmed. The apps always render it as text, never HTML.
 */
export function cleanCommentText(s) {
    return s
        .replace(/\r\n?/g, '\n')
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f‪-‮⁦-⁩]/g, '')
        .trim();
}
const CommentBody = z
    .string()
    .max(MAX_COMMENT_LENGTH * 2)
    .transform(cleanCommentText)
    .pipe(z.string().min(1, 'Write a comment').max(MAX_COMMENT_LENGTH, `Comments can be at most ${MAX_COMMENT_LENGTH} characters`));
export const CommentAttachment = z.object({
    id: z.uuid(),
    name: z.string(),
    contentType: CommentAttachmentType,
    size: z.number().int(),
    status: z.enum(['uploading', 'ready']),
    /** A 5-minute signed link; null until the upload is complete. */
    url: z.string().nullable(),
});
export const DesignCommentReply = z.object({
    id: z.uuid(),
    author: PersonRef.nullable(),
    /** "Summit Homes" for a client, the workspace's name for the team. */
    org: z.string(),
    byClient: z.boolean(),
    body: z.string(),
    /** Team-only note (never sent to a client at all). */
    internal: z.boolean(),
    /** Posted by replying to a comment email (docs/adr/0070): shown with a "via email" label. */
    viaEmail: z.boolean(),
    attachments: z.array(CommentAttachment),
    createdAt: z.string(),
});
export const DesignComment = z.object({
    id: z.uuid(),
    setId: z.uuid(),
    screenId: z.uuid(),
    screenTitle: z.string(),
    versionNumber: z.number().int(),
    /**
     * Made on an earlier version and not resolved yet (docs/adr/0069): shown on this version marked "from v{n}", with its
     * pin on the screen that replaced the one it was made on.
     */
    fromVersion: z.number().int().nullable().optional(),
    /** Carried over, but the screen it was on is not in this version: listed, with no pin. */
    screenRemoved: z.boolean().optional(),
    number: z.number().int(),
    x: z.number(),
    y: z.number(),
    /** An area's size; null for a pin. */
    w: z.number().nullable(),
    h: z.number().nullable(),
    author: PersonRef.nullable(),
    org: z.string(),
    byClient: z.boolean(),
    body: z.string(),
    /** Team only: clients never see it (never sent to them at all). */
    internal: z.boolean(),
    status: CommentStatus,
    /** The task made from it (team only; null for clients). */
    task: z.object({ id: z.uuid(), key: z.string(), done: z.boolean() }).nullable(),
    replies: z.array(DesignCommentReply),
    attachments: z.array(CommentAttachment),
    /** What the reader may do with it. */
    can: z.object({ reply: z.boolean(), note: z.boolean(), statuses: z.array(CommentStatus), makeTask: z.boolean() }),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const DesignCommentList = z.object({ items: z.array(DesignComment), urlsExpireAt: z.string() });
const Fraction = z.number().min(0).max(1);
export const CreateDesignCommentInput = z
    .object({
    screenId: z.uuid(),
    x: Fraction,
    y: Fraction,
    w: z.number().min(0.005).max(1).optional(),
    h: z.number().min(0.005).max(1).optional(),
    body: CommentBody,
    attachmentIds: z.array(z.uuid()).max(MAX_COMMENT_ATTACHMENTS).optional(),
    /**
     * Team only: a comment clients never see. Comments made while the set is internal only are always internal; on a
     * shared set the team's comments are visible to clients unless this is set.
     */
    internal: z.boolean().optional(),
})
    .refine((v) => (v.w === undefined) === (v.h === undefined), { message: 'An area needs a width and a height', path: ['w'] })
    .refine((v) => v.w === undefined || (v.x + v.w <= 1.000001 && v.y + v.h <= 1.000001), { message: 'The area must sit inside the screen', path: ['w'] });
export const ReplyToDesignCommentInput = z.object({
    body: CommentBody,
    /** An internal note: the team only. */
    internal: z.boolean().default(false),
    attachmentIds: z.array(z.uuid()).max(MAX_COMMENT_ATTACHMENTS).optional(),
});
export const SetDesignCommentStatusInput = z.object({ status: CommentStatus });
export const MakeTaskFromCommentInput = z.object({
    assigneeUserId: z.uuid().nullable().optional(),
    dueDate: z.iso.date().nullable().optional(),
});
export const AddCommentAttachmentInput = z.object({
    name: z.string().trim().min(1).max(255),
    size: z.number().int().min(1).max(MAX_COMMENT_ATTACHMENT_MB * 1024 * 1024),
    contentType: CommentAttachmentType,
});
export const AddedCommentAttachment = z.object({ attachment: CommentAttachment, upload: UploadTarget });
export const RecordDesignViewInput = z.object({ screenId: z.uuid(), seconds: z.number().int().min(0).max(MAX_VIEW_SECONDS) });
// --- Access (SC-18) and sharing (SC-06) ------------------------------------------------------------
export const DesignShareSettings = z.object({
    allowComments: z.boolean(),
    allowApproval: z.boolean(),
    allowDownload: z.boolean(),
    watermark: z.boolean(),
    requireTwoStep: z.boolean(),
    feedbackDue: z.string().nullable(),
    reminders: z.boolean(),
    /** Clients may approve single screens instead of the whole set (docs/adr/0069). Off by default. */
    perScreenApproval: z.boolean().optional(),
    sharedAt: z.string().nullable(),
});
export const DesignPublicLink = z.object({
    id: z.uuid(),
    /** The full address, only in the answer that made the link (only its hash is kept). */
    url: z.string().nullable(),
    hasPassword: z.boolean(),
    expiresAt: z.string().nullable(),
    allowDownload: z.boolean(),
    watermark: z.boolean(),
    invitedOnly: z.boolean(),
    viewCount: z.number().int(),
    createdAt: z.string(),
});
export const DesignAccessPerson = z.object({
    userId: z.uuid(),
    name: z.string(),
    /** Clients only: the address they were invited at. Team members show their workspace. */
    email: z.string().nullable(),
    kind: z.enum(['team', 'client']),
    role: DesignAccessRole,
    /** Clients: the grant to change or remove. */
    grantId: z.uuid().nullable(),
    /** Clients: invited and not signed in yet. */
    invited: z.boolean(),
});
export const DesignAccess = z.object({
    setId: z.uuid(),
    setName: z.string(),
    workspaceName: z.string(),
    visibility: DesignVisibility,
    people: z.array(DesignAccessPerson),
    settings: DesignShareSettings,
    publicLink: DesignPublicLink.nullable(),
    /** The person may change who has access (the team). */
    canManage: z.boolean(),
});
export const InviteToDesignSetInput = z.object({
    emails: z.array(z.email().max(254).transform((e) => e.trim().toLowerCase())).min(1).max(20),
    role: DesignGrantRole.default('comment'),
    message: z.string().trim().max(2000).optional(),
    feedbackDue: z.iso.date().nullable().optional(),
    /** Send the "Designs ready for review" email (the default). */
    sendEmail: z.boolean().default(true),
});
export const UpdateDesignGrantInput = z.object({ role: DesignGrantRole });
export const UpdateDesignSharingInput = z
    .object({
    visibility: DesignVisibility.optional(),
    allowComments: z.boolean().optional(),
    allowApproval: z.boolean().optional(),
    allowDownload: z.boolean().optional(),
    watermark: z.boolean().optional(),
    requireTwoStep: z.boolean().optional(),
    feedbackDue: z.iso.date().nullable().optional(),
    reminders: z.boolean().optional(),
    perScreenApproval: z.boolean().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to change' });
const LinkPassword = z.string().min(8, 'Use at least 8 characters').max(200);
export const CreateDesignPublicLinkInput = z.object({
    invitedOnly: z.boolean().default(true),
    password: LinkPassword.nullable().optional(),
    expiresAt: z.iso.datetime({ offset: true }).nullable().optional(),
    allowDownload: z.boolean().default(false),
    watermark: z.boolean().default(false),
});
export const UpdateDesignPublicLinkInput = z
    .object({
    invitedOnly: z.boolean().optional(),
    password: LinkPassword.nullable().optional(),
    expiresAt: z.iso.datetime({ offset: true }).nullable().optional(),
    allowDownload: z.boolean().optional(),
    watermark: z.boolean().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to change' });
/** View tracking (the set's Share tab). */
export const DesignViewer = z.object({
    person: PersonRef.nullable(),
    email: z.string().nullable(),
    /** A public link's anonymous views have no person. */
    label: z.string(),
    firstAt: z.string().nullable(),
    lastAt: z.string().nullable(),
    seconds: z.number().int(),
    screens: z.array(z.object({ screenId: z.uuid(), title: z.string(), seconds: z.number().int() })),
});
export const DesignViews = z.object({ opened: z.array(DesignViewer), notOpened: z.array(z.object({ name: z.string(), email: z.string() })) });
export const DesignMentionables = z.object({ items: z.array(PersonRef) });
// --- The guest's app (SC-17) and review page (SC-07) ------------------------------------------------
export const SharedDesignSetCard = z.object({
    id: z.uuid(),
    name: z.string(),
    kind: DesignSetKind,
    status: DesignSetStatus,
    currentVersion: z.number().int(),
    workspaceName: z.string(),
    role: DesignGrantRole,
    screenCount: z.number().int(),
    commentCount: z.number().int(),
    /** Team replies on the guest's own comments they have not seen yet (unread design notifications). */
    repliesForYou: z.number().int(),
    feedbackDue: z.string().nullable(),
    /** Comments allowed and not approved: "Needs your review". */
    needsReview: z.boolean(),
    covers: z.array(z.object({ url: z.string(), width: z.number().int(), height: z.number().int() })),
});
export const SharedDesignSetList = z.object({ items: z.array(SharedDesignSetCard), urlsExpireAt: z.string() });
export const ReviewDesignSet = z.object({
    id: z.uuid(),
    name: z.string(),
    kind: DesignSetKind,
    devicePreset: DevicePreset,
    deviceWidth: z.number().int(),
    deviceHeight: z.number().int(),
    status: DesignSetStatus,
    startScreenId: z.uuid().nullable(),
    versionNumber: z.number().int(),
    clientName: z.string(),
    workspaceName: z.string(),
    sharedAt: z.string().nullable(),
    feedbackDue: z.string().nullable(),
    /** The reader's role on this set. */
    role: DesignAccessRole,
    canComment: z.boolean(),
    allowDownload: z.boolean(),
    /** The reader's email to lay over the screens, when the set asks for a watermark. */
    watermark: z.string().nullable(),
    /** Approve designs is offered (docs/adr/0069): the reader may approve and this version is not approved yet. */
    canApprove: z.boolean().optional(),
    /** Clients may approve single screens. */
    perScreenApproval: z.boolean().optional(),
    /** This version is approved and locked. */
    locked: z.boolean().optional(),
    /** Screens of this version already approved one by one. */
    approvedScreenIds: z.array(z.uuid()).optional(),
    /** Who approved this version, when it is approved. */
    approval: z.object({ name: z.string(), at: z.string() }).nullable().optional(),
    /** A print set's page size, layout and proof margins (docs/adr/0072). */
    print: PrintSpec.nullable().optional(),
    /** The print-ready PDF can be downloaded by this reader (an approved print set; clients when downloads are allowed). */
    printPdf: z.boolean().optional(),
    screens: z.array(DesignScreen),
    hotspots: z.array(Hotspot),
    urlsExpireAt: z.string(),
});
// --- Public (no session) ---------------------------------------------------------------------------
export const DesignInviteInfo = z.object({
    /** Where the page goes once the account exists: this workspace (switched to) and this set's review page. */
    workspaceId: z.uuid(),
    setId: z.uuid(),
    workspaceName: z.string(),
    setName: z.string(),
    kind: DesignSetKind,
    inviterName: z.string(),
    /** The address the invitation was sent to: the account is made for it and it cannot be changed. */
    email: z.string(),
    screenCount: z.number().int(),
    covers: z.array(z.object({ url: z.string(), width: z.number().int(), height: z.number().int() })),
});
/** A public link: either "sign in first" (invited people only) or the guest-safe set to view. */
export const PublicDesignLink = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('sign_in'), setId: z.uuid(), workspaceName: z.string() }),
    z.object({ mode: z.literal('view'), set: ReviewDesignSet }),
]);
/** The header a password-protected public link's password travels in (never the URL). */
export const PUBLIC_LINK_PASSWORD_HEADER = 'x-chapsi-link-password';
//# sourceMappingURL=showcase-sharing.js.map